// Import your custom property entries.
// The entry is a text input field with logic attached to create,
// update and delete the "spell" property.
import caseProps, { processActionsProps, processActivityActionsProps, processFilterCategoryProps, processFiltersProps, processFormProps, processFormsProps, processViewProps } from './parts/CaseProps';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import processGeneralProps from './parts/ProcessProps';

const LOW_PRIORITY = 500;


/**
 * A provider with a `#getGroups(element)` method
 * that exposes groups for a diagram element.
 *
 * @param {PropertiesPanel} propertiesPanel
 * @param {Function} translate
 */
export default function CasePropertiesProvider(this: any, propertiesPanel: any, translate: any) {

  // API ////////

  /**
   * Return the groups provided for the given element.
   *
   * @param {DiagramElement} element
   *
   * @return {(Object[]) => (Object[])} groups middleware
   */
   this.getGroups = function(element: any) {

    /**
     * We return a middleware that modifies
     * the existing groups.
     *
     * @param {Array<{ id?: string }>} groups
     *
     * @return {Array<{ id?: string }>} modified groups
     */
    // Define a type for groups with entries
    type Group = { id?: string; entries?: any[] };

    return function(groups: Array<Group>) {
      const generalGroup = groups.find(g => g?.id === 'general');
      if (is(element, 'bpmn:Task')) {
        // groups.push(createCaseGroup(element, translate));
        if (generalGroup && Array.isArray(generalGroup.entries)) {
          generalGroup.entries.push(...
            caseProps(element)
          );
        }
      }
      if (is(element, 'bpmn:Process')) {
        if (generalGroup && Array.isArray(generalGroup.entries)) {
          generalGroup.entries.push(...
            processGeneralProps(element)
          );
         groups.push(createActionsGroup(element, translate));
         groups.push(createActivityActionsGroup(element, translate));
         groups.push(createFormsGroup(element, translate));
         groups.push(createFormGroup(element, translate));
         groups.push(createViewGroup(element, translate));
         groups.push(createFilterCategoryGroup(element, translate));
         groups.push(createFiltersGroup(element, translate));
        }
      }

      return groups;
    };
  };


  // registration ////////

  // Register our custom case properties provider.
  // Use a lower priority to ensure it is loaded after
  // the basic BPMN properties.
  propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

CasePropertiesProvider.$inject = [ 'propertiesPanel', 'translate' ];

function createActionsGroup(element: any, translate :any) {

  const group = {
    id: 'actions',
    label: translate('Actions'),
    entries: processActionsProps(element),
    tooltip: translate('Define actions properties')
  };

  return group;
}

function createActivityActionsGroup(element: any, translate :any) {

  const group = {
    id: 'activityActions',
    label: translate('Activity Actions'),
    entries: processActivityActionsProps(element),
    tooltip: translate('Define activity actions properties')
  };

  return group;
}

function createFormsGroup(element: any, translate :any) {

  const group = {
    id: 'forms',
    label: translate('Forms'),
    entries: processFormsProps(element),
    tooltip: translate('Define forms properties')
  };

  return group;
}

function createFormGroup(element: any, translate :any) {

  const group = {
    id: 'form',
    label: translate('Form'),
    entries: processFormProps(element),
    tooltip: translate('Define form properties')
  };

  return group;
}

function createViewGroup(element: any, translate :any) {

  const group = {
    id: 'view',
    label: translate('View'),
    entries: processViewProps(element),
    tooltip: translate('Define view properties')
  };

  return group;
}

function createFilterCategoryGroup(element: any, translate :any) {

  const group = {
    id: 'filterCategory',
    label: translate('Filter Category'),
    entries: processFilterCategoryProps(element),
    tooltip: translate('Define filter category properties')
  };

  return group;
}

function createFiltersGroup(element: any, translate :any) {

  const group = {
    id: 'filters',
    label: translate('Filters'),
    entries: processFiltersProps(element),
    tooltip: translate('Define filters properties')
  };

  return group;
}